// File: /src/hooks/useProjectsData.js

import { useState, useCallback, useEffect } from 'react';
import apiClient from '../services/apiClient';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { AuthError } from '../utils/auth';
import { useAuth } from '../contexts/AuthContext';

let cachedProjects = null;
let cachedClients = null;
let projectsInitialized = false;
let inFlightPromise = null;
let cacheToken = null;

const mapProjectsWithClientNames = (projects = [], clients = []) => {
	return projects.map((project) => ({
		...project,
		clientName:
			clients.find((client) => client.id === project.clientId)?.name ||
			'Cliente não encontrado',
	}));
};

export const useProjectsData = () => {
	const { token } = useAuth();
	const [projects, setProjectsState] = useState(cachedProjects ?? []);
	const [clients, setClientsState] = useState(cachedClients ?? []);
	const [loading, setLoading] = useState(!projectsInitialized);
	const [error, setError] = useState(null);

	const updateProjectsCache = useCallback((updater) => {
		setProjectsState((prev) => {
			const next =
				typeof updater === 'function' ? updater(prev) : updater;
			cachedProjects = next;
			return next;
		});
	}, []);

	const updateClientsCache = useCallback((updater) => {
		setClientsState((prev) => {
			const next =
				typeof updater === 'function' ? updater(prev) : updater;
			cachedClients = next;
			return next;
		});
	}, []);

	const fetchProjectsData = useCallback(async (force = false, tokenSnapshot = cacheToken) => {
		if (!tokenSnapshot) {
			return;
		}

		if (!force && cachedProjects && cachedClients) {
			setProjectsState(cachedProjects);
			setClientsState(cachedClients);
			setLoading(false);
			return;
		}

		if (inFlightPromise && !force) {
			await inFlightPromise;
			return;
		}

		setLoading(true);

		const loadData = async () => {
			try {
				const [clientsRes, projectsRes] = await Promise.all([
					apiClient.get(API_ENDPOINTS.CLIENTS),
					apiClient.get(API_ENDPOINTS.PROJECTS),
				]);

				const fetchedClients = clientsRes.data || [];
				const fetchedProjects = mapProjectsWithClientNames(
					projectsRes.data || [],
					fetchedClients,
				);

				if (cacheToken !== tokenSnapshot) {
					return;
				}

				cachedClients = fetchedClients;
				cachedProjects = fetchedProjects;
				projectsInitialized = true;

				setClientsState(fetchedClients);
				setProjectsState(fetchedProjects);
				setError(null);
			} catch (err) {
				if (err instanceof AuthError) {
					return;
				}
				console.error('Erro ao carregar dados de projetos:', err);
				setError(err);
			} finally {
				if (cacheToken === tokenSnapshot) {
					setLoading(false);
				}
			}
		};

		inFlightPromise = loadData();
		await inFlightPromise;
		inFlightPromise = null;
	}, []);

	useEffect(() => {
		if (!token) {
			cachedProjects = null;
			cachedClients = null;
			projectsInitialized = false;
			cacheToken = null;
			inFlightPromise = null;
			setProjectsState([]);
			setClientsState([]);
			setLoading(false);
			return;
		}

		if (cacheToken && cacheToken !== token) {
			cachedProjects = null;
			cachedClients = null;
			projectsInitialized = false;
			inFlightPromise = null;
			setProjectsState([]);
			setClientsState([]);
			setLoading(true);
			fetchProjectsData(true, token).catch(() => {
				setLoading(false);
			});
		} else if (!projectsInitialized) {
			setLoading(true);
			fetchProjectsData(true, token).catch(() => {
				setLoading(false);
			});
		}

		cacheToken = token;
	}, [token, fetchProjectsData]);

	return {
		projects,
		clients,
		loading,
		error,
		refreshProjectsData: (force = false) => fetchProjectsData(force, cacheToken),
		setProjectsCache: updateProjectsCache,
		setClientsCache: updateClientsCache,
	};
};

export default useProjectsData;
